// ═════════════════════════════════════════════════════════════════════════════
// Annual OKR — Live Review agenda
//
// ⚠️ IDENTICAL IN TWO REPOSITORIES, like the socket store beside it. It imports
// nothing app-specific for that reason.
//
// Turns §7.3's `remarks` into the group tree `SummaryGroups` renders — the
// Annual OKR counterpart of `liveReviewAgenda.js`.
//
// ⚠️ THE AGENDA IS `SummaryGroups`, NOT A STACK OF COMMENT BOXES. An earlier
// pass rendered each remark as an `eblr-section-comment` — the block EXW uses
// for a section comment on the CANVAS — and put the whole Objective statement
// in its `…-badge`, which is a two-word chip reading "Section comment" in EXW.
// The result was a paragraph of uppercase teal where a tag belongs. The agenda
// on both sibling screens is the summary panel; this builds its input.
// ═════════════════════════════════════════════════════════════════════════════

// `SummaryPanel`'s `SummaryItem` compares against these exact strings, so they
// are imported in the app that has them and restated here only because this
// file must not import anything app-specific. Keep in step with
// `StrategyCanvasSummary`'s `ITEM_KIND`.
export const AGENDA_ITEM_KIND = {
  BLOCK: "block",
  SECTION: "section",
  OVERALL: "overall",
};

// ⚠️ CSS MODIFIER NAMES, not colour words. `SummaryPanel.scss` defines exactly
// `--teal`, `--dark`, `--core` and `--dna`; anything else interpolates into a
// class matching no rule and the rail renders unstyled with no error anywhere.
const OBJECTIVES_COLOR = "teal";
const OVERALL_COLOR = "core";

const OVERALL_GROUP_KEY = "__overall__";

// Stable id per remark, so React keys and any future scroll-to target agree.
export const agendaTargetFor = (remark) =>
  remark.annual_objective_id != null
    ? `objective-${remark.annual_objective_id}-${remark.remark_type}-${remark.id}`
    : `overall-${remark.id}`;

/**
 * @param remarks §7.3's `remarks.remarks` array.
 *
 * ⚠️ ONE GROUP FOR THE OBJECTIVES, not one per Objective. EXW groups by its four
 * WIG sections because those are its canvas bands; Annual OKR's canvas is a flat
 * list, so a group per Objective would put a rail and a heading around every
 * single row.
 */
export const buildLiveAgenda = (remarks, objectives) => {
  // ⚠️ NO BAND, BUT A REAL NAME — and the second half of that is a correction.
  //
  // `SummaryPanel` renders `band` ABOVE `name`, so setting both to "Company
  // OKRs" printed it twice. The band is rightly gone: this whole feature IS the
  // Company OKRs (§10 — no Leader or Team OKRs), so a band naming the only kind
  // of OKR that exists groups one thing under its own name.
  //
  // But an earlier pass emptied `name` TOO, and that was wrong for a reason
  // visible the moment it rendered: `SummaryPanel` draws the group's item count
  // in the SAME row as the heading, so an empty name left a coloured rail and a
  // bare "1" chip floating over a blank line. A heading row still exists
  // whether or not there is text in it — the only choice is whether it says
  // something.
  //
  // Matches `AnnualOkrReviewSummary.buildReviewSummary` exactly. These two
  // panels are the same rail on two screens and the coach moves between them in
  // one sitting; they must not disagree about their own headings. Change both.
  const objectivesGroup = {
    key: "objectives",
    band: null,
    name: "Company OKRs",
    color: OBJECTIVES_COLOR,
    items: [],
  };
  // Named for the same reason, and this one also draws a real distinction — a
  // remark about the board as a whole rather than about a row. Spelled
  // "Overall assessment" to match the coach review panel's wording.
  const overallGroup = {
    key: OVERALL_GROUP_KEY,
    band: null,
    name: "Overall assessment",
    color: OVERALL_COLOR,
    items: [],
  };

  (remarks || []).forEach((remark) => {
    const isOverall = remark.annual_objective_id == null;
    const kind = isOverall ? AGENDA_ITEM_KIND.OVERALL : AGENDA_ITEM_KIND.BLOCK;

    (isOverall ? overallGroup : objectivesGroup).items.push({
      id: agendaTargetFor(remark),
      kind,
      title: remark.objective_label || remark.label || "",
      // ── An undiscussed row is "To discuss". Full stop. ──────────────────
      // This is the MEETING's state, not the Objective's verdict — the same
      // rule EXW's agenda arrived at after getting it wrong twice. Every row
      // here is a remark, and the agenda only ever contains flagged Objectives
      // and comments: one accepted cleanly produces no remark and so has no row
      // at all. Showing the Objective's own verdict here produced a green
      // "Accepted" pill on exactly the row still asking to be talked through,
      // while `remarks_remaining` kept the Lock button disabled with nothing on
      // screen explaining why.
      //
      // Scoped rows stay null: an overall comment has no Objective behind it,
      // and `SummaryItem` refuses a pill there anyway.
      status: kind === AGENDA_ITEM_KIND.BLOCK ? "to_discuss" : null,
      discussed: !!remark.discussed,
      // A `remark_type: "objective"` row is the coach flagging an Objective
      // with nothing written (§4.4/§7.3), so it carries no comment — the row
      // is the point. Wrapping `null` in an array would render an empty card.
      comments: remark.comment_text
        ? [
            {
              coach_comment_id: remark.id,
              comment_text: remark.comment_text,
              created_at: remark.created_at || null,
            },
          ]
        : [],
      // Carried so the panel can offer "Mark discussed" without re-deriving
      // which remark a row came from.
      remarkType: remark.remark_type,
      remarkId: remark.id,
      // For the board-order sort below.
      objectiveId: remark.annual_objective_id,
    });
  });

  // ⚠️ SORTED HERE, NOT TAKEN IN ARRIVAL ORDER. The server returns `remarks`
  // with the discussed ones last, so the moment a row was accepted it jumped to
  // the bottom of the agenda — on both screens, mid-sentence. EXW and Strategy
  // Canvas don't do that: a row stays where it is and only its pill changes to
  // "Discussed". So the order is fixed client-side: the Objective's position on
  // the board, then the remark id to keep two remarks on one Objective (a flag
  // and a comment) in a stable order. Overall comments sort by id alone.
  //
  // This only ORDERS the server's list; it never adds, drops or re-counts a
  // row, so `remarks_remaining` and the visible agenda still agree.
  const boardIndex = new Map(
    (objectives || []).map((objective, index) => [
      String(objective.annual_objective_id),
      index,
    ]),
  );
  const compareIds = (a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  };
  const byBoardOrder = (a, b) => {
    // An Objective removed during the session has no board position; it
    // sorts after the rest rather than being dropped.
    const ia = boardIndex.get(String(a.objectiveId)) ?? Infinity;
    const ib = boardIndex.get(String(b.objectiveId)) ?? Infinity;
    if (ia !== ib) return ia - ib;
    return compareIds(a.remarkId, b.remarkId);
  };
  objectivesGroup.items.sort(byBoardOrder);
  overallGroup.items.sort((a, b) => compareIds(a.remarkId, b.remarkId));

  // Overall assessment FIRST — JK asked for the coach's overall read before
  // the item-by-item notes on Strategy Canvas, and EXW matches (H01).
  return [overallGroup, objectivesGroup].filter((g) => g.items.length > 0);
};
